import { Breadcrumbs, Card } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useLocation, useNavigate, useParams } from 'react-router'
import {
  useGetDatasetDetails,
  useGetDatasetVersionsList
} from '@/queries/datasetQueries'
import { RouterPath, replacePathParams } from '@/router/paths'
import { Dataset, DatasetVersion } from '@/types/dataset'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { PageWithFixedToolbar } from '../layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { BreadcrumbLink } from '../ui-shared/Breadcrumbs/BreadcrumbLink'
import { BreadcrumbsSkeleton } from '../ui-shared/Breadcrumbs/BreadcrumbsSkeleton'
import { ErrorWithReload } from '../ui-shared/ErrorWithReload/ErrorWithReload'
import { DatasetToolbar } from './DatasetToolbar/DatasetToolbar'
import { DatasetToolbarSkeleton } from './DatasetToolbar/DatasetToolbarSkeleton'
import { UploadableMediaListHandler } from './UploadableMediaList/UploadableMediaListHandler'

export const DatasetDetailsPage = () => {
  const navigate = useNavigate()
  const { datasetId } = useParams()
  const location = useLocation()

  const [activeVersion, setActiveVersion] = useState<DatasetVersion | null>(
    null
  )

  const { datasetListParams } = (location.state || {}) as {
    datasetListParams?: string
  }

  const {
    data: dataset,
    isLoading,
    refetch: refetchDatasetDetails,
    isError: isDatasetError
  } = useGetDatasetDetails(datasetId || '', !!datasetId)

  const { data: versionsData, isFetching: isVersionsFetching } =
    useGetDatasetVersionsList(
      datasetId || '',
      {
        limit: MAX_ITEMS_WITHOUT_PAGINATION,
        ordering: '-created_at'
      },
      {
        enabled: !!datasetId
      }
    )

  const versions = useMemo(() => versionsData?.results || [], [versionsData])

  useEffect(() => {
    if (versions.length) {
      setActiveVersion(versions[0])
    }
  }, [versions])

  const handleVersionChange = (versionId: string) => {
    setActiveVersion(
      versions.find((version) => version.id === versionId) || null
    )
  }

  const handleDatasetCloned = (newDataset: Dataset) => {
    void navigate(
      replacePathParams(RouterPath.datasetDetails, {
        datasetId: newDataset.id
      })
    )
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        dataset ? (
          <Breadcrumbs>
            <BreadcrumbLink
              label={<FormattedMessage id="navLinks.datasets" />}
              to={{
                pathname: RouterPath.datasets,
                search: datasetListParams
              }}
            />

            <span>{dataset.name}</span>
          </Breadcrumbs>
        ) : (
          <BreadcrumbsSkeleton />
        )
      }
    >
      {isLoading && !dataset && (
        <Card withBorder>
          <DatasetToolbarSkeleton />
        </Card>
      )}

      {isDatasetError && !dataset && (
        <ErrorWithReload onReload={() => void refetchDatasetDetails()}>
          <FormattedMessage id="datasets.data.error" />
        </ErrorWithReload>
      )}

      {dataset && (
        <>
          <Card px="lg" mb="sm" withBorder>
            <DatasetToolbar
              dataset={dataset}
              versions={versions}
              activeVersion={activeVersion || undefined}
              isVersionPending={isVersionsFetching}
              onVersionChange={handleVersionChange}
            />
          </Card>

          {activeVersion && activeVersion.dataset === dataset.id && (
            <UploadableMediaListHandler
              key={activeVersion.id}
              dataset={dataset}
              version={activeVersion}
              onDatasetCloned={handleDatasetCloned}
            />
          )}
        </>
      )}
    </PageWithFixedToolbar>
  )
}
